import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePeopleAdmin } from '../context/PeopleAdminContext';
import {
  fetchEmployees,
  fetchEmployeesPage,
  fetchEmployeesWithAssignmentCounts,
  createEmployee,
  updateEmployee,
  deactivateEmployee,
  reactivateEmployee,
  linkEmployeeToUser,
  unlinkEmployeeFromUser
} from '../services/employeeService';

/**
 * Enhanced hook for managing paginated employee data with filtering
 * @param {Object} options - Hook configuration options
 * @param {number} options.pageSize - Number of items per page (default: 50)
 * @param {boolean} options.enablePagination - Enable pagination (default: true)
 * @returns {Object} Paginated employee data, mutations, and utility functions
 */
export function useEmployeesPaginated(options = {}) {
  const { pageSize = 50, enablePagination = true } = options;
  const queryClient = useQueryClient();
  const { clientId } = usePeopleAdmin();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageTokens, setPageTokens] = useState([null]); // Array of tokens for each page
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    isActive: undefined,
    employmentType: undefined,
    hasUser: undefined
  });

  // Current page token
  const currentToken = pageTokens[currentPage] || null;

  // Query for paginated employees with assignment counts
  const {
    data: pageData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['employees', clientId, currentPage, currentToken, pageSize, filters],
    queryFn: async () => {
      if (!enablePagination) {
        // Fetch all employees with assignment counts for client-side pagination
        const allEmployees = await fetchEmployeesWithAssignmentCounts(clientId);
        return { items: allEmployees, nextToken: null, hasMore: false };
      }
      // For paginated data, fetch employees first then enrich with assignment counts
      const pageResult = await fetchEmployeesPage(clientId, currentToken, pageSize);
      
      // Import assignment service to fetch assignment counts
      const { fetchLocationAssignments, fetchOrgUnitAssignments } = await import('../services/assignmentService');
      
      // Enrich employees with assignment counts
      const enrichedItems = await Promise.all(
        pageResult.items.map(async (employee) => {
          try {
            const [locationAssignments, orgUnitAssignments] = await Promise.all([
              fetchLocationAssignments(employee.id, clientId),
              fetchOrgUnitAssignments(employee.id, clientId)
            ]);
            
            const allAssignments = [...locationAssignments, ...orgUnitAssignments];
            const assignmentsByLevel = {};
            let locationCount = 0;
            
            // Count location assignments
            locationAssignments.forEach(() => {
              locationCount++;
            });
            
            // Count org unit assignments by level
            orgUnitAssignments.forEach(assignment => {
              const orgLevel = assignment.orgLevel || 'ORGLEVEL_1';
              assignmentsByLevel[orgLevel] = (assignmentsByLevel[orgLevel] || 0) + 1;
            });
            
            // Add location count to the assignmentsByLevel object
            assignmentsByLevel.locations = locationCount;
            
            return {
              ...employee,
              assignments: allAssignments,
              assignmentsByLevel,
              totalAssignments: allAssignments.length
            };
          } catch (error) {
            return {
              ...employee,
              assignments: [],
              assignmentsByLevel: {},
              totalAssignments: 0
            };
          }
        })
      );
      
      return { ...pageResult, items: enrichedItems };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true,
    enabled: !!clientId
  });

  // Apply client-side filtering
  const filteredEmployees = useMemo(() => {
    if (!pageData?.items) return [];
    
    let filtered = [...pageData.items];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(e => 
        e.firstName?.toLowerCase().includes(searchLower) ||
        e.lastName?.toLowerCase().includes(searchLower) ||
        e.email?.toLowerCase().includes(searchLower)
      );
    }
    
    // Status filter
    if (filters.isActive !== undefined) {
      filtered = filtered.filter(e => 
        filters.isActive ? e.isActive !== false : e.isActive === false
      );
    }
    
    // Employment type filter
    if (filters.employmentType) {
      filtered = filtered.filter(e => e.employmentType === filters.employmentType);
    }
    
    // RadixDash user filter
    if (filters.hasUser !== undefined) {
      filtered = filtered.filter(e => 
        filters.hasUser ? !!e.userId : !e.userId
      );
    }
    
    return filtered;
  }, [pageData?.items, filters]);

  // Navigation functions
  const goToNextPage = useCallback(() => {
    if (pageData?.nextToken && pageData.hasMore) {
      const newTokens = [...pageTokens];
      if (currentPage + 1 >= newTokens.length) {
        newTokens.push(pageData.nextToken);
        setPageTokens(newTokens);
      }
      setCurrentPage(currentPage + 1);
    }
  }, [pageData, currentPage, pageTokens]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(0);
  }, []);

  const resetPagination = useCallback(() => {
    setCurrentPage(0);
    setPageTokens([null]);
  }, []);

  // Filter update function
  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    resetPagination(); // Reset to first page when filters change
  }, [resetPagination]);

  // Create employee mutation
  const createEmployeeMutation = useMutation({
    mutationFn: (employeeData) => createEmployee({ ...employeeData, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      resetPagination();
    },
  });

  // Update employee mutation
  const updateEmployeeMutation = useMutation({
    mutationFn: async ({ id, updates, closeAssignments, userAccessDecision }) => {
      // Handle termination flow if specified
      if (closeAssignments) {
        // Add logic to close assignments if needed
        // This would be implemented when assignment management is added
      }
      const updatedEmployee = await updateEmployee(id, updates);
      return updatedEmployee;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      return data;
    },
  });

  // Deactivate employee mutation with cascade logic
  const deactivateEmployeeMutation = useMutation({
    mutationFn: async ({ employeeId, deactivateUser }) => {
      // First deactivate the employee
      const result = await deactivateEmployee(employeeId);
      
      // If requested and employee has a user, deactivate the user too
      if (deactivateUser && result.userId) {
        // This would call the user deactivation service
        // await deactivateUser(result.userId);
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      queryClient.invalidateQueries({ queryKey: ['users', clientId] });
    },
  });

  // Reactivate employee mutation
  const reactivateEmployeeMutation = useMutation({
    mutationFn: (id) => reactivateEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
    },
  });

  // Link employee to user mutation
  const linkToUserMutation = useMutation({
    mutationFn: ({ employeeId, userId }) => linkEmployeeToUser(employeeId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      queryClient.invalidateQueries({ queryKey: ['users', clientId] });
    },
  });

  // Unlink employee from user mutation (backward compatibility)
  const unlinkFromUserMutation = useMutation({
    mutationFn: (employeeId) => unlinkEmployeeFromUser(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', clientId] });
      queryClient.invalidateQueries({ queryKey: ['users', clientId] });
    },
  });


  // Utility functions
  const getActiveCount = useCallback(() => {
    return filteredEmployees.filter(e => e.isActive !== false).length;
  }, [filteredEmployees]);

  const getInactiveCount = useCallback(() => {
    return filteredEmployees.filter(e => e.isActive === false).length;
  }, [filteredEmployees]);

  const getEmployeesWithoutUsersCount = useCallback(() => {
    return filteredEmployees.filter(e => !e.userId).length;
  }, [filteredEmployees]);

  return {
    // Data
    employees: filteredEmployees,
    totalCount: pageData?.items?.length || 0,
    filteredCount: filteredEmployees.length,
    
    // Pagination state
    currentPage,
    hasNextPage: !!pageData?.hasMore,
    hasPreviousPage: currentPage > 0,
    pageSize,
    
    // Navigation
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    resetPagination,
    
    // Filters
    filters,
    updateFilters,
    
    // Mutations
    createEmployee: createEmployeeMutation.mutateAsync,
    updateEmployee: updateEmployeeMutation.mutateAsync,
    deactivateEmployee: deactivateEmployeeMutation.mutateAsync,
    reactivateEmployee: reactivateEmployeeMutation.mutateAsync,
    linkToUser: linkToUserMutation.mutateAsync,
    unlinkFromUser: unlinkFromUserMutation.mutateAsync,
    
    // Loading states
    isLoading,
    isError,
    error,
    isCreating: createEmployeeMutation.isPending,
    isUpdating: updateEmployeeMutation.isPending,
    isDeactivating: deactivateEmployeeMutation.isPending,
    isReactivating: reactivateEmployeeMutation.isPending,
    isLinking: linkToUserMutation.isPending,
    isUnlinking: unlinkFromUserMutation.isPending,
    
    // Utility functions
    getActiveCount,
    getInactiveCount,
    getEmployeesWithoutUsersCount,
    refetch
  };
}